import React, { useState, useEffect } from 'react';
import {

  Dialog,
  DialogContent,
  TextField,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  Movie as MovieIcon,
} from '@mui/icons-material';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { getColors, createTypographyStyle, glassStyles } from '../../tokens/designTokens';
import { font } from '../../utils/fontHelper';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';
interface MovieNoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (note: string) => void;
  movieTitle: string;
  moviePoster?: string;
  currentNote?: string;
  isLoading?: boolean;
}

const MovieNoteDialog: React.FC<MovieNoteDialogProps> = ({
  open,
  onClose,
  onSave,
  movieTitle,
  moviePoster,
  currentNote = '',
  isLoading = false,
}) => {
  const theme = useTheme();
  const colors = getColors(theme.palette.mode === 'dark');
  const [note, setNote] = useState(currentNote);

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isKeyboardOpen, bottomPadding, scrollIntoViewOnFocus } = useKeyboardAware();

  useEffect(() => {
    setNote(currentNote);
  }, [currentNote]);

  const handleSave = () => {
    onSave(note.trim());
  };

  const handleClose = () => {
    setNote(currentNote); // Reset to original note
    onClose();
  };

  // Shared content component
  const NoteDialogContent = (
    <>
      {/* Header */}
      <BottomSheetDraggableHeader sx={{
        p: 3,
        pb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          {/* Movie Poster */}
          <Box sx={{
            width: 40,
            height: 30,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            bgcolor: colors.background.secondary,
            display: 'flex',
            alignItems: 'bottom',
            justifyContent: 'center',
          }}>
            {moviePoster ? (
              <Box
                component="img"
                src={moviePoster}
                alt={movieTitle}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <MovieIcon sx={{ color: colors.text.tertiary, fontSize: 20 }} />
            )}
          </Box>

          {/* Movie Title */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontFamily: font(),
              fontSize: 18,
              fontWeight: 600,
              color: colors.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {movieTitle}
            </Typography>

          </Box>
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            color: colors.text.tertiary,
            '&:hover': {
              bgcolor: colors.background.secondary,
            },
          }}
        >
          <Close />
        </IconButton>
      </BottomSheetDraggableHeader>

      {/* Content */}
      <Box sx={{
        p: 3,
        pt: 1,
        pb: isKeyboardOpen ? `${bottomPadding + 16}px` : 3,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        transition: 'padding-bottom 0.25s ease-out',
      }}>
        <TextField
          autoFocus={!isMobile} // Don't auto-focus on mobile to prevent keyboard popup on open
          multiline
          rows={4}
          fullWidth
          placeholder="Write a note about this movie..."
          value={note}
          onChange={(e) => { if (e.target.value.length <= 500) setNote(e.target.value); }}
          onFocus={scrollIntoViewOnFocus}
          variant="outlined"
          helperText={`${note.length}/500`}
          inputProps={{ maxLength: 500 }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              height: '100%',
              alignItems: 'flex-start',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 2,
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: colors.brand.secondary },
              '&.Mui-focused fieldset': {
                borderColor: colors.brand.secondary,
                borderWidth: '2px'
              },
            },
            '& .MuiInputBase-input': {
              color: colors.text.primary,
              fontSize: 16, // Prevent iOS zoom on focus
              fontFamily: font(),
              '&::placeholder': {
                color: colors.text.tertiary,
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      {/* Actions */}
      <Box sx={{
        p: 3,
        pt: 2,
        display: 'flex',
        gap: 2,
        flexShrink: 0,
      }}>
        <CustomButton
          onClick={handleClose}
          variant="contained"
          semantic="tertiary"
          disabled={isLoading}
          sx={{ flex: 1 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          onClick={handleSave}
          variant="contained"
          semantic="brand"
          disabled={isLoading}
          sx={{ flex: 1 }}
        >
          {currentNote ? "Update Note" : "Add Note"}
        </CustomButton>
      </Box>
    </>
  );

  // Mobile: Drawer (not SwipeableDrawer — avoids swipe-to-dismiss vs scroll/keyboard; see MarkAsWatchedDialog)
  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={handleClose}
        maxHeight="85dvh"
        zIndex={1200}
      >
        <Box sx={{ minHeight: '40dvh', display: 'flex', flexDirection: 'column', flex: 1 }}>
          {NoteDialogContent}
        </Box>
      </BottomSheet>
    );
  }

  // Tablet/Desktop: Centered Dialog
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1200,
        '& .MuiDialog-paper': {
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...glassStyles.bottomSheet(theme.palette.mode === 'dark'),
          // Remove default padding from Dialog
          margin: 2,
          width: 'calc(100% - 32px)',
          maxWidth: '500px',
          borderRadius: 2, // Override glass styles borderRadius
        },
      }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {NoteDialogContent}
      </DialogContent>
    </Dialog>
  );
};

export default MovieNoteDialog; 
 